import axios from 'axios'
import Cookies from 'js-cookie'
import { BASE_URL, fetchAssignedInstitutes } from './ApiEndpoints'

export function buildHomeworkHeaders() {
  const instituteId = Cookies.get('institute_id')
  const userCookie = Cookies.get('user')
  let token = ''
  let instituteFromUser = null
  if (userCookie) {
    try {
      const parsed = JSON.parse(userCookie)
      token = parsed?.token || ''
      if (parsed?.institute_id != null && parsed.institute_id !== '') {
        instituteFromUser = parsed.institute_id
      }
    } catch {
      token = ''
    }
  }
  const headers = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  }
  if (token) headers.Authorization = `Bearer ${token}`
  const instRaw = instituteId || instituteFromUser
  const instNum = Number(instRaw)
  if (Number.isFinite(instNum) && instNum > 0) {
    headers['institute-Id'] = instNum
  }
  return headers
}

/** Headers for multipart upload (no Content-Type — browser sets boundary). */
export function buildHomeworkMultipartHeaders() {
  const headers = buildHomeworkHeaders()
  delete headers['Content-Type']
  return headers
}

export function resolveInstituteId() {
  const fromCookie = Cookies.get('institute_id')
  const n = Number(fromCookie)
  if (Number.isFinite(n) && n > 0) return n
  try {
    const parsed = JSON.parse(Cookies.get('user') || '{}')
    const u = Number(parsed?.institute_id)
    if (Number.isFinite(u) && u > 0) return u
  } catch {
    /* ignore */
  }
  return null
}

export function resolveAcademicSessionId() {
  const fromCookie = Cookies.get('academic_session_id')
  const n = Number(fromCookie)
  if (Number.isFinite(n) && n > 0) return n
  return null
}

export function getHomeworkErrorMessage(error) {
  const status = error?.response?.status
  const data = error?.response?.data
  if (status === 422) {
    const errs = data?.errors
    if (errs && typeof errs === 'object') {
      const msgs = Object.values(errs).flat().filter(Boolean)
      if (msgs.length) return msgs.join(' ')
    }
    return data?.message || 'Validation failed.'
  }
  return data?.message || data?.result || error?.message || 'Failed to save homework.'
}

export function parseHomeworkIdFromResponse(res) {
  const raw = res?.data
  const id =
    raw?.data?.id ??
    raw?.data?.homework?.id ??
    raw?.id ??
    raw?.homework_id
  const n = Number(id)
  return Number.isFinite(n) && n > 0 ? n : null
}

export function parseHomeworkMediaUploadResponse(res) {
  const raw = res?.data ?? {}
  const url =
    raw?.url ??
    raw?.data?.attachment_url ??
    raw?.data?.url ??
    ''
  const path = raw?.path ?? raw?.data?.attachment_path ?? ''
  return { url: String(url), path: String(path), message: raw?.message ?? '' }
}

/**
 * Upload homework attachment to R2 via POST /homework/{id}/upload-media.
 * Field name: `media` (alias endpoint also accepts `attachment`).
 */
export async function uploadHomeworkMedia(homeworkId, file) {
  if (!homeworkId || !file) {
    throw new Error('Homework id and file are required for upload.')
  }

  const formData = new FormData()
  formData.append('media', file)

  const headers = buildHomeworkMultipartHeaders()
  const url = `${BASE_URL}/homework/${homeworkId}/upload-media`

  try {
    const res = await axios.post(url, formData, { headers })
    const parsed = parseHomeworkMediaUploadResponse(res)
    if (!parsed.url) {
      throw new Error(parsed.message || 'Upload succeeded but no attachment URL was returned.')
    }
    return parsed
  } catch (primaryErr) {
    const formDataAlt = new FormData()
    formDataAlt.append('attachment', file)
    try {
      const res = await axios.post(
        `${BASE_URL}/homework/${homeworkId}/upload-attachment`,
        formDataAlt,
        { headers },
      )
      const parsed = parseHomeworkMediaUploadResponse(res)
      if (!parsed.url) {
        throw new Error(parsed.message || 'Upload succeeded but no attachment URL was returned.')
      }
      return parsed
    } catch {
      throw primaryErr
    }
  }
}

export async function createHomeworkRequest(body) {
  return axios.post(`${BASE_URL}/homework`, body, {
    headers: buildHomeworkHeaders(),
  })
}

export function parseHomeworkOne(res) {
  const raw = res?.data
  if (raw?.data && typeof raw.data === 'object' && !Array.isArray(raw.data)) {
    return raw.data
  }
  if (raw && typeof raw === 'object' && !Array.isArray(raw)) return raw
  return null
}

export async function fetchHomeworkById(id) {
  return axios.get(`${BASE_URL}/homework/${id}`, {
    headers: buildHomeworkHeaders(),
  })
}

export async function updateHomeworkRequest(id, body) {
  return axios.put(`${BASE_URL}/homework/${id}`, body, {
    headers: buildHomeworkHeaders(),
  })
}

export async function deleteHomeworkRequest(id) {
  return axios.delete(`${BASE_URL}/homework/${id}`, {
    headers: buildHomeworkHeaders(),
  })
}

export function parseHomeworkListResponse(res) {
  const raw = res?.data
  if (Array.isArray(raw?.data)) return raw.data
  if (Array.isArray(raw)) return raw
  return []
}

export function normalizeHomeworkRow(item) {
  if (!item || typeof item !== 'object') return item
  const className = item.class_name || '—'
  const subjectName = item.subject_name || ''
  const attachmentUrl = item.attachment_url || ''
  let mediaLabel = '—'
  if (attachmentUrl) {
    try {
      const path = attachmentUrl.split('?')[0]
      mediaLabel = path.split('/').pop() || 'Attachment'
    } catch {
      mediaLabel = 'Attachment'
    }
  }
  return {
    ...item,
    class_name_only: className,
    class_name: subjectName ? `${className} — ${subjectName}` : className,
    media_label: mediaLabel,
    attachment_url: attachmentUrl,
  }
}

export async function fetchHomeworkList(params = {}) {
  const instituteId = resolveInstituteId()
  const query = { ...params }
  if (instituteId && query.institute_id == null) {
    query.institute_id = instituteId
  }
  return axios.get(`${BASE_URL}/homework`, {
    headers: buildHomeworkHeaders(),
    params: query,
  })
}

/** GET /parents/students/homework — parents role only. */
export async function fetchParentStudentsHomework(params = {}) {
  return axios.get(`${BASE_URL}/parents/students/homework`, {
    headers: buildHomeworkHeaders(),
    params,
  })
}

/** GET /parents/students — linked students for the logged-in parent. */
export async function fetchParentStudents() {
  return axios.get(`${BASE_URL}/parents/students`, {
    headers: buildHomeworkHeaders(),
  })
}

/** GET /parents/holidays — holidays grouped by institute / student. */
export async function fetchParentHolidays(params = {}) {
  const query = {}
  if (params.student_id != null && params.student_id !== '') {
    query.student_id = Number(params.student_id)
  }
  const type = String(params.holiday_type || params.type || '').trim().toUpperCase()
  if (type && type !== 'ALL') {
    query.holiday_type = type
  }
  return axios.get(`${BASE_URL}/parents/holidays`, {
    headers: buildHomeworkHeaders(),
    params: query,
  })
}

export function parseParentHolidaysResponse(res) {
  const raw = res?.data?.data
  const groups = Array.isArray(raw) ? raw : raw && typeof raw === 'object' ? [raw] : []
  const studentsMap = new Map()
  const institutesMap = new Map()
  const holidaysMap = new Map()

  groups.forEach((group) => {
    const instituteId = group?.institute_id
    const session = group?.session || ''

    if (instituteId != null) {
      institutesMap.set(String(instituteId), {
        institute_id: instituteId,
        session,
      })
    }

    const students = Array.isArray(group?.students) ? group.students : []
    students.forEach((student) => {
      const id = student?.id ?? student?.student_id
      if (id == null) return
      studentsMap.set(String(id), {
        id,
        name: student?.name ?? student?.student_name ?? '',
        admission_number: student?.admission_number ?? '',
        institute_id: student?.institute_id ?? instituteId,
      })
    })

    const holidays = Array.isArray(group?.holidays) ? group.holidays : []
    const groupStudentIds = students
      .map((student) => student?.id ?? student?.student_id)
      .filter((id) => id != null)
      .map((id) => String(id))

    holidays.forEach((holiday) => {
      const key = [
        holiday?.id ?? '',
        holiday?.date ?? '',
        holiday?.title ?? '',
        instituteId ?? '',
      ].join('|')
      const existing = holidaysMap.get(key)
      if (existing) {
        const mergedStudentIds = new Set([
          ...(existing.student_ids || []),
          ...groupStudentIds,
        ])
        existing.student_ids = Array.from(mergedStudentIds)
        return
      }
      holidaysMap.set(key, {
        ...holiday,
        institute_id: holiday?.institute_id ?? instituteId,
        session: holiday?.session ?? session,
        holiday_type: holiday?.holiday_type ?? holiday?.type ?? '',
        student_ids: groupStudentIds,
      })
    })
  })

  return {
    institutes: Array.from(institutesMap.values()),
    students: Array.from(studentsMap.values()).sort((a, b) =>
      (a.name || '').localeCompare(b.name || ''),
    ),
    holidays: Array.from(holidaysMap.values()),
  }
}

/** After parent login, sync institute/session cookies from linked students / holidays API. */
export async function syncParentContextAfterLogin() {
  try {
    const res = await fetchParentHolidays()
    const { institutes } = parseParentHolidaysResponse(res)
    const firstInstitute = institutes[0]
    if (firstInstitute?.institute_id != null) {
      Cookies.set('institute_id', String(firstInstitute.institute_id), { expires: 7 })
    }
    if (firstInstitute?.session) {
      Cookies.set('session_name', firstInstitute.session, { expires: 7 })
    }
    return
  } catch {
    /* fall through to students list */
  }

  try {
    const res = await fetchParentStudents()
    const list = Array.isArray(res?.data?.data) ? res.data.data : []
    const firstStudent = list[0]
    const instituteId = firstStudent?.institute_id
    if (instituteId != null) {
      Cookies.set('institute_id', String(instituteId), { expires: 7 })
    }
  } catch {
    /* optional */
  }
}

/** GET /students/{studentId}/homework */
export async function fetchStudentHomework(studentId) {
  return axios.get(`${BASE_URL}/students/${studentId}/homework`, {
    headers: buildHomeworkHeaders(),
  })
}

export function parseStudentHomeworkResponse(res) {
  const raw = res?.data?.data ?? res?.data ?? {}
  const homework = Array.isArray(raw?.homework) ? raw.homework : []
  return {
    student_id: raw.student_id,
    student_name: raw.student_name,
    admission_number: raw.admission_number,
    institute_id: raw.institute_id,
    class_id: raw.class_id,
    class_name: raw.class_name,
    homework: homework.map((item) =>
      normalizeHomeworkRow({
        ...item,
        class_id: item.class_id ?? raw.class_id,
        class_name: item.class_name ?? raw.class_name,
      }),
    ),
  }
}

export function parseParentStudentsHomeworkResponse(res) {
  const raw = res?.data
  const groups = Array.isArray(raw?.data) ? raw.data : Array.isArray(raw) ? raw : []
  const flat = []
  groups.forEach((group) => {
    const hwList = Array.isArray(group?.homework) ? group.homework : []
    hwList.forEach((hw) => {
      flat.push(
        normalizeHomeworkRow({
          ...hw,
          student_id: group.student_id,
          student_name: group.student_name,
          admission_number: group.admission_number,
          class_id: hw.class_id ?? group.class_id,
          class_name: hw.class_name ?? group.class_name,
        }),
      )
    })
  })
  return flat
}

export async function fetchAcademicSessionIdForInstitute(instituteId) {
  if (!instituteId) return null
  const cached = resolveAcademicSessionId()
  if (cached) return cached
  try {
    const res = await fetchAssignedInstitutes()
    const list = Array.isArray(res?.data?.data) ? res.data.data : []
    const matched = list.find(
      (row) => String(row?.institute_id ?? row?.id ?? '') === String(instituteId),
    )
    const sessionId = matched?.active_session?.id ?? matched?.active_session_id
    const n = Number(sessionId)
    if (Number.isFinite(n) && n > 0) {
      Cookies.set('academic_session_id', String(n), { expires: 7 })
      return n
    }
  } catch {
    /* optional */
  }
  return null
}
